import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth, AUTH_STATE } from "../contexts/AuthContext";

/**
 * Fase 1L.8C — Layout dedicado ao Admin Console.
 *
 * Contém APENAS a marca, o indicador "Administração global", o botão
 * "Voltar para empresas" e a ação de Logout. Nenhum menu, workspace,
 * projeto ou elemento da Unika é renderizado — o Admin Console é
 * isolado visual e navigacionalmente do ambiente operacional.
 *
 * Guard próprio: se o estado não for ``AUTHED_ADMIN_ONLY``, redireciona
 * imediatamente (nunca renderiza a área administrativa em outro
 * contexto).
 */
export default function AdminLayout({ children }) {
  const navigate = useNavigate();
  const { state, exitAdminConsole, logoutAdminConsole } = useAuth();

  useEffect(() => {
    if (state === AUTH_STATE.LOADING) return;
    if (state === AUTH_STATE.AUTHED_ADMIN_ONLY) return;
    if (state === AUTH_STATE.NEEDS_TENANT) {
      navigate("/selecionar-empresa", { replace: true });
    } else if (state === AUTH_STATE.AUTHED) {
      navigate("/dashboard", { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  }, [state, navigate]);

  if (state !== AUTH_STATE.AUTHED_ADMIN_ONLY) {
    return null;
  }

  const onBackToCompanies = async () => {
    try {
      await exitAdminConsole();
      navigate("/selecionar-empresa", { replace: true });
    } catch {
      toast.error("Não foi possível voltar às empresas. Tente novamente.");
    }
  };

  const onLogout = async () => {
    await logoutAdminConsole();
    navigate("/login", { replace: true });
  };

  return (
    <div
      className="min-h-screen bg-slate-50 flex flex-col"
      data-testid="admin-layout"
    >
      <header
        className="h-16 bg-white border-b border-slate-200 flex items-center px-6 gap-4"
        data-testid="admin-layout-header"
      >
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-slate-900 grid place-items-center text-white font-heading font-bold">
            R
          </div>
          <div className="leading-tight">
            <div className="font-heading font-semibold text-slate-900 tracking-tight">
              Ruma Flow
            </div>
            <div
              className="text-[10px] uppercase tracking-widest text-slate-400"
              data-testid="admin-layout-scope-label"
            >
              Administração global
            </div>
          </div>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <button
            type="button"
            onClick={onBackToCompanies}
            data-testid="admin-layout-back-to-companies"
            className="h-9 px-3 rounded-lg border border-slate-200 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Voltar para empresas
          </button>
          <button
            type="button"
            onClick={onLogout}
            data-testid="admin-layout-logout"
            className="h-9 px-3 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800"
          >
            Sair
          </button>
        </div>
      </header>
      <main className="flex-1 p-6" data-testid="admin-layout-main">
        {children}
      </main>
    </div>
  );
}
